<script setup lang="ts">
import type { ImageCategory } from "@/rvapi";
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useImageStore } from "@/stores/images";
import type { PnFileUpload } from "@postnord/web-components/components/pn-file-upload";
import type { PnUploadFileItem } from "@postnord/web-components/types/globals/types";

const router = useRouter();
const imageStore = useImageStore();
const uploadLog = ref("");
const hidden = ref(false);
const filecount = ref(0);

const loadedState = ref(0);
const categories = ref([] as ImageCategory[]);
const selectedcat = ref(0);
const englishDescription = ref("");
const swedishDescription = ref("");
const englishDescriptionError = ref("");
const swedishDescriptionError = ref("");
const isError = ref(false);
const errorDescription = ref("");
const showSpinner = ref(false);
const setcategory = async (e: { target: { value: string } }) => {
  selectedcat.value = parseInt(e.target.value, 10);
  if (loadedState.value < 2) {
    loadedState.value = 2;
  }
};
const languages = ref({
  swedish: true,
  english: true,
  norwegian: true,
  danish: true,
} as Record<string, boolean>);

const filesToUpload = ref<PnUploadFileItem[]>([]);

const getLanguages = computed(() => {
  const l = [];
  if (languages.value.swedish) {
    l.push("swedish");
  }
  if (languages.value.english) {
    l.push("english");
  }
  if (languages.value.danish) {
    l.push("danish");
  }
  if (languages.value.norwegian) {
    l.push("norwegian");
  }
  return l;
});

const languageChange = (e: any) => {
  const lang = e.target.id;
  languages.value[lang] = e.target.checked;
};

const hasLanguage = computed(() => {
  return getLanguages.value.length > 0;
});

const filesAdded = async (e: any) => {
  if (e.detail.length > 0) {
    const errorFiles = e.detail.filter((d: any) => d.error);
    if (errorFiles.length > 0) {
      loadedState.value = 2;
      return;
    } else {
      resetError();
      filecount.value = e.detail.length;
      loadedState.value += 1;
      filesToUpload.value = e.detail;
    }
  } else if (e.detail.length === 0) {
    filecount.value = e.detail.length;
    resetError();
    filesToUpload.value = e.detail;
    loadedState.value = 2;
  }
};

const uploadAllFiles = async () => {
  const isValidationError =
    swedishDescription.value === "" || englishDescription.value === "";
  if (swedishDescription.value === "") {
    swedishDescriptionError.value = "Please enter swedish alternative text";
  }
  if (englishDescription.value === "") {
    englishDescriptionError.value = "Please enter english alternative text";
  }

  if (!isValidationError) {
    resetError();
    for (const file of filesToUpload.value) {
      await uploadFile(file);
    }
  }
};

const uploadFile = async (file: PnUploadFileItem) => {
  const lang = getLanguages.value;
  uploadLog.value += `Saving image ${file.filename}\n`;
  try {
    showSpinner.value = true;
    await imageStore.saveImage(undefined, {
      attribution: "",
      attributionType: "",
      filename: file.filename,
      base64: file.base64,
      hidden: hidden.value,
      categories: [selectedcat.value],
      languages: lang,
      imageDescription: [
        {
          language: "swedish",
          description: swedishDescription.value,
        },
        {
          language: "english",
          description: englishDescription.value,
        },
      ],
    } as any);

    filecount.value -= 1;
    uploadLog.value += `Remaining files: ${filecount.value}\n`;
    if (filecount.value === 0) {
      router.push("images");
    }
  } catch (e) {
    isError.value = true;
    errorDescription.value =
      "There was an error in uploading one or all the images";
    window.scrollTo(0, 0);
  } finally {
    showSpinner.value = false;
  }
};

const resetError = () => {
  isError.value = false;
  errorDescription.value = "";
  window.scrollTo(0, 0);
  swedishDescriptionError.value = "";
  englishDescriptionError.value = "";
};

onMounted(async () => {
  resetError();
  categories.value = await imageStore.getImageCategories();
  loadedState.value += 1;
  if (categories.value.length > 0) {
    selectedcat.value = categories.value[0].categoryId;
    loadedState.value = 2;
  }
});
</script>

<template>
  <pn-toast
    appearance="error"
    v-show="isError"
    :text="errorDescription"
  ></pn-toast>
  <h2>Upload new images</h2>
  <div :class="[{ spinner: showSpinner }]">
    <pn-spinner size="3" v-show="showSpinner" />
  </div>
  <h3>Step 1, select category:</h3>
  <div v-show="loadedState >= 1">
    <pn-select @change="setcategory" label="Select category">
      <option
        v-for="c in categories"
        :key="c.categoryId"
        :selected="c.categoryId === selectedcat"
        :value="c.categoryId"
      >
        {{ c.swedish || c.english || c.danish || c.norwegian }}
      </option>
    </pn-select>
  </div>
  <div v-show="loadedState >= 2">
    <h3>Step 2, select languages:</h3>
    <p v-show="!uploadLog">
      <label for="swedish">
        <pn-checkbox
          checkboxid="swedish"
          :checked="languages.swedish"
          @change="languageChange"
        />
        Swedish
      </label>
      <label for="english">
        <pn-checkbox
          checkboxid="english"
          :checked="languages.english"
          @change="languageChange"
        />
        English
      </label>
      <label for="danish">
        <pn-checkbox
          checkboxid="danish"
          :checked="languages.danish"
          @change="languageChange"
        />
        Danish
      </label>
      <label for="norwegian">
        <pn-checkbox
          checkboxid="norwegian"
          :checked="languages.norwegian"
          @change="languageChange"
        />
        Norwegian
      </label>
    </p>
    <p>
      <label for="hidden">
        <pn-checkbox
          checkboxid="hidden"
          :checked="hidden"
          @change="hidden = $event.target.checked"
        />
        Upload images as hidden
      </label>
    </p>
  </div>
  <div v-show="loadedState >= 2 && hasLanguage">
    <h3>Step 3: Upload</h3>
    <div v-show="uploadLog">
      <pre>{{ uploadLog }}</pre>
    </div>
    <div v-show="!getLanguages.length">
      <pn-toast closable="false" appearance="warning">
        Must select at least one language to upload
      </pn-toast>
    </div>
    <div v-show="getLanguages.length && !uploadLog">
      <span>Number of valid files uploaded {{ filesToUpload.length }}</span>
      <br /><br />
      <pn-file-upload
        accept=".png,.jpg"
        max-size="4mb"
        disabled="false"
        multiple="yes"
        limit="10"
        show-spinner="true"
        helpertext="You can upload multiple images at the same time(Max Image dimensions 2539 * 1807 px)."
        @filesAdded="filesAdded"
      >
      </pn-file-upload>
    </div>
  </div>
  <div v-show="loadedState >= 3">
    <h3>Step 4, add alternative text (for accessibility purposes):</h3>
    <pn-input
      placeholder="Swedish alternative text (required*)"
      v-model="swedishDescription"
      :error="swedishDescriptionError"
    />
    <br /><br />
    <pn-input
      placeholder="English alternative text (required*)"
      v-model="englishDescription"
      :error="englishDescriptionError"
    />
    <br />
    <pn-button @click="uploadAllFiles" class="uploadButton"
      >Upload Image</pn-button
    >
  </div>
</template>

<style lang="scss" scoped>
img {
  border: 1px dashed gray;
  border-radius: 1rem;
  width: 300px;
}
.icon {
  width: 200px;
}

.fiex {
  display: flex;
  justify-content: center;
}

.uploadButton {
  margin-top: 15px;
}

.spinner {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
